// ═══════════════════════════════════════
// HEADER-FORM
// ═══════════════════════════════════════

import {
    sessionToken, currentId, currentPartitionMeta,
    setCurrentId, setCurrentSlug, setCurrentPartitionMeta,
    setHeaderFormActive, setPendingAction
} from './state.js';
import {
    highlightAll, getAbcContent,
    parseAbcDocument, stripMarkers,
    getHeadersContent, setHeadersContent,
    getVoiceHeaderContent, setVoiceHeaderContent,
    getVoiceContent, setVoiceContent, getVoiceCount,
    isVoiceHidden, setVoiceHidden,
} from './editor.js';
import { render } from './renderer.js';
import { openUserModal, authHeaders } from './common/auth.js';
import { openModal, closeModal } from './modal.js';
import { updateOwnerUI } from './navigation.js';
import { refreshAttachButtons } from './attachments.js';
import { ABC_DIRECTIVE_RULES, MANAGED_PRE_PREFIXES } from './abc-directives.js';
import { setActiveInstruments } from './instrument-context.js';
import { historyReset } from './history.js';
import { setKeyPickerValue, initKeyPickerFor, getKeyPickerInstance } from './key-picker.js';
import { setMeterPickerValue, initMeterPickerFor } from './meter-picker.js';
import { setTempoPickerValue, initTempoPickerFor } from './tempo-picker.js';
import { initUnitNotePickerFor, updateUnitNotePickerDefault } from './unit-note-picker.js';
import { t } from './i18n.js';

const INSTRUMENTS = window.INSTRUMENTS ?? [];

export const ICON_EYE = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>';

export const ICON_EYE_OFF = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

// ══════════════════════════════════════════════════════════════════════════════
// HELPERS — V:{n} et K: / [K:]
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Retourne la ligne V:{n} à placer en tête de voiceHeader.
 * Retourne null si program_abc contient déjà "V:{v}" (la ligne sera
 * générée par _computeContextualDirectives via postK).
 */
function _voicePrefixLine(voiceIdx, instrProgram) {
    if (instrProgram && /V:\{v\}/i.test(instrProgram)) return null;
    return `V:${voiceIdx + 1}`;
}

/**
 * Fusionne des attributs supplémentaires (ex: 'stem=up') sur une ligne V:N.
 * Les attributs déjà présents ne sont pas dupliqués.
 * Ex: _mergeVoiceAttrs('V:1 name="Cornemuse"', 'stem=up')
 *   → 'V:1 name="Cornemuse" stem=up'
 */
function _mergeVoiceAttrs(vLine, attrs) {
    if (!attrs) return vLine;
    const parts = attrs.trim().split(/\s+/);
    let result = vLine;
    for (const part of parts) {
        const key = part.split('=')[0];
        // N'ajouter que si la clé n'est pas déjà présente
        if (!new RegExp(`\\b${key}=`).test(result)) {
            result = result.trimEnd() + ' ' + part;
        }
    }
    return result;
}

/**
 * Pour les voix 1+ : retire K: du voiceHeader et retourne [K:valeur]
 * à injecter dans le corps après la ligne V:{n}.
 * Pour la voix 0 : ne fait rien (K: reste dans le header).
 */
function _applyKPlacement(voiceIdx, headerLines, kLineOrValue) {
    if (voiceIdx === 0) {
        return { headerLines, inlineK: null };
    }
    const filtered  = headerLines.filter(l => !/^K:/i.test(l));
    // Retirer le préfixe 'K:' si kLineOrValue est une ligne complète
    const kContent  = /^K:/i.test(kLineOrValue) ? kLineOrValue.slice(2) : kLineOrValue;
    return { headerLines: filtered, inlineK: `[K:${kContent}]` };
}


/**
 * Construit une ligne K: complète avec clef= et stafflines= si non-défaut.
 * Ex: _buildKLine('G', 'bass', 1)    → 'K:G clef=bass stafflines=1'
 *     _buildKLine('none', 'treble', 5) → 'K:none'
 *     _buildKLine('', 'bass', 1)      → 'K: clef=bass stafflines=1'
 */
function _buildKLine(keyAbc, clef, stafflines) {
    const clefPart       = (clef && clef !== 'treble')               ? ` clef=${clef}`            : '';
    const stafflinesPart = (stafflines != null && stafflines !== 5)  ? ` stafflines=${stafflines}` : '';
    return `K:${keyAbc}${clefPart}${stafflinesPart}`;
}

/**
 * Injecte [K:...] dans le corps de voix après la ligne V:{n},
 * en remplaçant tout [K:...] déjà présent à cet endroit.
 */
function _injectInlineK(body, inlineK) {
    // Supprimer tout [K:...] existant dans le corps
    const clean = body.replace(/\[K:[^\]]*\]/g, '').replace(/\n{3,}/g, '\n\n');
    const lines  = clean.split('\n');
    const vIdx   = lines.findIndex(l => /^V:\d+/i.test(l));
    if (vIdx !== -1) {
        // Supprimer les lignes vides immédiatement après V:{n}
        let next = vIdx + 1;
        while (next < lines.length && lines[next].trim() === '') {
            lines.splice(next, 1);
        }
        // Insérer [K:...] juste après V:{n}
        lines.splice(vIdx + 1, 0, inlineK);
    } else {
        lines.unshift(inlineK);
    }
    return lines.join('\n');
}

// ══════════════════════════════════════════════════════════════════════════════
// DOM — Construction des blocs de voix
// ══════════════════════════════════════════════════════════════════════════════

export function buildVoiceBlocks(doc) {
    const container = document.getElementById('voices-container');
    if (!container) return;
    container.innerHTML = '';

    const count = Math.max(doc.voiceHeaders.length, doc.voices.length, 1);
    for (let i = 0; i < count; i++) {
        const instrId = doc.voiceInstrumentIds?.[i] ?? null;
        container.appendChild(
            _createVoiceBlock(i, doc.voiceHeaders[i] ?? '', doc.voices[i] ?? '', instrId)
        );
    }
    _refreshActiveInstruments();
    _refreshVisibilityButtons();
}

export function appendVoiceBlock(voiceHeader = '', voiceBody = '') {
    const container = document.getElementById('voices-container');
    if (!container) return;
    const idx = container.querySelectorAll('.voice-block').length;
    container.appendChild(_createVoiceBlock(idx, voiceHeader, voiceBody));
    _refreshActiveInstruments();
    _refreshVisibilityButtons();
}

function _createVoiceBlock(idx, headerText, bodyText, instrId = null) {
    const label = _voiceLabel(headerText, idx, instrId);

    const block = document.createElement('div');
    block.className     = 'voice-block';
    block.dataset.voice = idx;
    if (instrId != null) block.dataset.instrumentId = instrId;

    const canEdit = currentPartitionMeta?.is_owner ?? (!currentId && !!sessionToken);

    block.innerHTML = `
        <div class="voice-header">
            <button type="button" class="voice-toggle" aria-expanded="true">
                <span class="voice-toggle-arrow">▾</span>
                <span class="voice-toggle-label">${_esc(label)}</span>
            </button>
            ${canEdit ? `
            <button type="button" class="voice-move-up"   title="Monter la voix">↑</button>
            <button type="button" class="voice-move-down" title="Descendre la voix">↓</button>
            <button type="button" class="voice-settings"  title="Paramètres de la voix">⚙</button>
            ` : ''}
            <button type="button" class="voice-visibility"
                    title="${_esc(t('hf.voice_hide'))}" aria-pressed="false">${ICON_EYE}</button>
        </div>
        <div class="voice-body">
            <div class="voice-header-display" id="abc-voice-header-${idx}" aria-hidden="true"></div>
            <div class="abc-voice" id="abc-voice-${idx}"
                 contenteditable="${canEdit ? 'true' : 'false'}" spellcheck="false"></div>
        </div>
    `;

    block.querySelector(`#abc-voice-header-${idx}`).textContent = stripMarkers(headerText).trim();
    block.querySelector(`#abc-voice-${idx}`).textContent        = stripMarkers(bodyText).trim();

    block.querySelector('.voice-toggle').addEventListener('click', () => {
        const body     = block.querySelector('.voice-body');
        const arrow    = block.querySelector('.voice-toggle-arrow');
        const expanded = body.style.display !== 'none';
        body.style.display = expanded ? 'none' : '';
        arrow.textContent  = expanded ? '▸' : '▾';
        block.querySelector('.voice-toggle').setAttribute('aria-expanded', String(!expanded));
        block.classList.toggle('voice-closed', expanded);
    });

    block.querySelector('.voice-move-up')?.addEventListener('click', () => {
        const currentIdx = parseInt(block.dataset.voice);
        if (currentIdx > 0) _swapVoices(currentIdx - 1, currentIdx);
    });

    block.querySelector('.voice-move-down')?.addEventListener('click', () => {
        const currentIdx = parseInt(block.dataset.voice);
        const total = document.querySelectorAll('.voice-block').length;
        if (currentIdx < total - 1) _swapVoices(currentIdx, currentIdx + 1);
    });

    block.querySelector('.voice-settings')?.addEventListener('click', () => {
        openVoiceParams(idx);
    });

    block.querySelector('.voice-visibility')?.addEventListener('click', () => {
        _toggleVoiceVisibility(parseInt(block.dataset.voice));
    });

    return block;
}

function _visibleVoiceCount() {
    const count = getVoiceCount();
    let n = 0;
    for (let i = 0; i < count; i++) if (!isVoiceHidden(i)) n++;
    return n;
}

/**
 * Met à jour tous les boutons œil :
 *   — partition mono-voix          → bouton retiré de l'affichage ;
 *   — dernière voix encore visible → bouton verrouillé (pas de hover,
 *     pas de clic, tooltip explicatif).
 * Prévient ensuite le menu « Voix » de la barre d'actions.
 */
function _refreshVisibilityButtons() {
    const count   = getVoiceCount();
    const visible = _visibleVoiceCount();

    for (let i = 0; i < count; i++) {
        const block = document.querySelector(`.voice-block[data-voice="${i}"]`);
        const btn   = block?.querySelector('.voice-visibility');
        if (!btn) continue;

        // Une seule voix dans la partition : rien à masquer
        if (count <= 1) {
            btn.hidden = true;
            continue;
        }
        btn.hidden = false;

        const hidden = isVoiceHidden(i);
        const locked = !hidden && visible <= 1;

        btn.innerHTML = hidden ? ICON_EYE_OFF : ICON_EYE;
        btn.setAttribute('aria-pressed',  String(hidden));
        btn.setAttribute('aria-disabled', String(locked));
        btn.classList.toggle('voice-visibility--locked', locked);
        btn.title = locked
            ? t('hf.voice_hide_last')
            : (hidden ? t('hf.voice_show') : t('hf.voice_hide'));
    }

    document.dispatchEvent(new CustomEvent('scorou:voices-changed'));
}

/**
 * Liste des voix pour le menu « Voix » de la barre d'actions.
 * Libellé « V1 Cornemuse » ; sans instrument identifié, « Voix 1 ».
 */
export function getVoiceVisibilityItems() {
    const count   = getVoiceCount();
    const visible = _visibleVoiceCount();
    const items   = [];

    for (let i = 0; i < count; i++) {
        const block      = document.querySelector(`.voice-block[data-voice="${i}"]`);
        const instrId    = block?.dataset.instrumentId ? parseInt(block.dataset.instrumentId) : null;
        const headerText = getVoiceHeaderContent(i);
        const instrLabel = instrId
            ? (INSTRUMENTS.find(x => x.id === instrId)?.label ?? _getInstrumentLabelFromHeader(headerText))
            : _getInstrumentLabelFromHeader(headerText);
        const hidden = isVoiceHidden(i);

        items.push({
            idx:    i,
            label:  instrLabel ? `V${i + 1} ${instrLabel}` : t('hf.voice_label', { n: i + 1 }),
            hidden,
            locked: !hidden && visible <= 1,
        });
    }
    return items;
}

/** Point d'entrée public du masquage (menu « Voix »). */
export function toggleVoiceVisibility(voiceIdx) {
    _toggleVoiceVisibility(voiceIdx);
}

/**
 * Masque / réaffiche une voix. La voix masquée est exclue du rendu abcjs,
 * de la lecture audio, de l'impression et du PDF — mais reste intégralement
 * conservée dans le contenu sauvegardé (getAbcContent).
 * On refuse de masquer la dernière voix visible.
 */
function _toggleVoiceVisibility(voiceIdx) {
    const hidden = isVoiceHidden(voiceIdx);
    if (!hidden && _visibleVoiceCount() <= 1) return;
    setVoiceHidden(voiceIdx, !hidden);
    _refreshVisibilityButtons();
    render();
}

function _swapVoices(idxA, idxB) {
    const blockA = document.querySelector(`.voice-block[data-voice="${idxA}"]`);
    const blockB = document.querySelector(`.voice-block[data-voice="${idxB}"]`);
    if (!blockA || !blockB) return;

    // Lire les contenus avant l'échange
    const headerA = getVoiceHeaderContent(idxA);
    const bodyA   = getVoiceContent(idxA);
    const instrA  = blockA.dataset.instrumentId ?? null;
    const hiddenA = isVoiceHidden(idxA);

    const headerB = getVoiceHeaderContent(idxB);
    const bodyB   = getVoiceContent(idxB);
    const instrB  = blockB.dataset.instrumentId ?? null;
    const hiddenB = isVoiceHidden(idxB);

    // Écrire les contenus croisés
    setVoiceHeaderContent(idxA, headerB);
    setVoiceContent(idxA, bodyB);
    if (instrB != null) blockA.dataset.instrumentId = instrB;
    else delete blockA.dataset.instrumentId;

    setVoiceHeaderContent(idxB, headerA);
    setVoiceContent(idxB, bodyA);
    if (instrA != null) blockB.dataset.instrumentId = instrA;
    else delete blockB.dataset.instrumentId;

    // Le masquage suit la voix, pas la position
    setVoiceHidden(idxA, hiddenB);
    setVoiceHidden(idxB, hiddenA);
    _refreshVisibilityButtons();

    // Renuméroiter V:N dans les headers et corps des deux voix
    _renumberVoice(idxA);
    _renumberVoice(idxB);

    // Rafraîchir les labels des deux blocs
    _updateVoiceLabel(idxA);
    _updateVoiceLabel(idxB);

    _refreshActiveInstruments();
    highlightAll();
    render();
}


/**
 * Met à jour le numéro V:N dans le header et le corps d'une voix
 * pour qu'il corresponde à son index réel (0-based → V:idx+1).
 */
function _renumberVoice(idx) {
    const newNum = idx + 1;

    // Header : remplacer la ligne V:N existante
    const header    = getVoiceHeaderContent(idx);
    const newHeader = header.replace(/^(V:)\d+/m, `$1${newNum}`);
    setVoiceHeaderContent(idx, newHeader);

    // Corps : remplacer la ligne V:N et le [K:] inline si présent
    const body    = getVoiceContent(idx);
    const newBody = body.replace(/^(V:)\d+/m, `$1${newNum}`);
    setVoiceContent(idx, newBody);
}

function _voiceLabel(headerText, idx, instrId = null) {
    const instrLabel = instrId
        ? (INSTRUMENTS.find(i => i.id === instrId)?.label ?? _getInstrumentLabelFromHeader(headerText))
        : _getInstrumentLabelFromHeader(headerText);
    const key  = _getHeaderLine(headerText, 'K') || '';
    const base = instrLabel || t('hf.voice_label', { n: idx + 1 });
    const full = key ? `${base} — ${key}` : base;
    return full.length > 30 ? full.slice(0, 29) + '…' : full;
}

function _getInstrumentLabelFromHeader(headerText) {
    const m = headerText.match(/%%MIDI program\s+(\d+)/);
    if (!m) return '';
    const num  = parseInt(m[1]);
    const inst = INSTRUMENTS.find(i => {
        const n = i.program.match(/%%MIDI program\s+(\d+)/);
        return n && parseInt(n[1]) === num;
    });
    return inst?.label ?? '';
}

function _getInstrumentNameFromHeader(headerText) {
    const m = headerText.match(/%%MIDI program\s+(\d+)/);
    if (!m) return '';
    const num  = parseInt(m[1]);
    const inst = INSTRUMENTS.find(i => {
        const n = i.program.match(/%%MIDI program\s+(\d+)/);
        return n && parseInt(n[1]) === num;
    });
    return inst?.name ?? '';
}

function _getHeaderLine(text, tag) {
    const m = text.match(new RegExp('^' + tag + ':(.*)$', 'm'));
    return m ? m[1].trim() : '';
}

function _getVoiceNameAttr(headerText) {
    const m = headerText.match(/^V:\d+[^"]*name="([^"]*)"/m);
    return m ? m[1] : '';
}


function _getVoiceClefAttr(headerText, bodyText = '') {
    // Cherche dans le header (K: standalone) puis dans le corps ([K:...] inline)
    const mHeader = headerText.match(/^K:[^\n]*\bclef=(\S+)/m);
    if (mHeader) return mHeader[1];
    const mInline = bodyText.match(/\[K:[^\]]*\bclef=([^\]\s]+)/);
    return mInline ? mInline[1] : '';
}

function _getVoiceStafflinesAttr(headerText, bodyText = '') {
    const mHeader = headerText.match(/^K:[^\n]*\bstafflines=(\d+)/m);
    if (mHeader) return parseInt(mHeader[1]);
    const mInline = bodyText.match(/\[K:[^\]]*\bstafflines=(\d+)/);
    return mInline ? parseInt(mInline[1]) : null;
}

function _getVoiceStemAttr(headerText) {
    const m = headerText.match(/^V:\d+[^\n]*\bstem=(up|down|auto)\b/m);
    return m ? m[1] : 'auto';
}

function _updateVoiceLabel(idx) {
    const block = document.querySelector(`.voice-block[data-voice="${idx}"]`);
    if (!block) return;
    const instrId    = block.dataset.instrumentId ? parseInt(block.dataset.instrumentId) : null;
    const headerText = getVoiceHeaderContent(idx);
    block.querySelector('.voice-toggle-label').textContent = _voiceLabel(headerText, idx, instrId);
}

// ══════════════════════════════════════════════════════════════════════════════
// DIRECTIVES CONTEXTUELLES
// ══════════════════════════════════════════════════════════════════════════════

function _computeContextualDirectives(selected, key, voiceNum = 1) {
    const slugs  = new Set(selected.map(i => i.name));
    const keyStr = (key || '').trim();

    const preKSet    = new Set();
    const postKLines = [];
    const voiceAttrParts = [];

    for (const rule of ABC_DIRECTIVE_RULES) {
        if (!rule.instruments.every(s => slugs.has(s))) continue;
        if (rule.key && !rule.key.test(keyStr)) continue;
        (rule.preK ?? []).forEach(l => preKSet.add(l.replace(/\{v\}/g, voiceNum)));
        (rule.postK ?? []).forEach(l => postKLines.push(l.replace(/\{v\}/g, voiceNum)));
        if (rule.voiceAttrs) voiceAttrParts.push(rule.voiceAttrs);
    }

    const postK = [];
    if (selected.length >= 1) {
        postK.push(selected[0].program.replace(/\{v\}/g, voiceNum));
        postKLines.forEach(l => postK.push(l));
    }

    return { preK: [...preKSet], postK, voiceAttrs: voiceAttrParts.join(' ') };
}

// ══════════════════════════════════════════════════════════════════════════════
// MISE À JOUR D'UNE VOIX
// ══════════════════════════════════════════════════════════════════════════════

export function updateVoiceFromForm(voiceIdx) {
    const headerText = getVoiceHeaderContent(voiceIdx);
    const lines      = headerText.split('\n');
    const kIdx       = lines.findIndex(l => /^K:/i.test(l));
    if (kIdx === -1) return;

    const key = lines[kIdx].replace(/^K:/i, '').trim();

    const managedRe = new RegExp(
        '^(' + MANAGED_PRE_PREFIXES.map(p => p.replace(/%/g, '%')).join('|') + ')\\b'
    );

    // Retirer les lignes gérées (preK) et la ligne V: existante
    const cleanedPreK = lines.slice(0, kIdx).filter(
        l => !managedRe.test(l) && !/^V:\d+/i.test(l)
    );

    let blockEnd = kIdx + 1;
    while (blockEnd < lines.length && /^(V:\d+|%%)/.test(lines[blockEnd])) blockEnd++;

    const instrName = _getInstrumentNameFromHeader(headerText);
    const instr     = INSTRUMENTS.find(i => i.name === instrName);
    const selected  = instr ? [instr] : [];

    const { preK, postK, voiceAttrs } = _computeContextualDirectives(selected, key, voiceIdx + 1);


    // V:{n} en tête sauf si program contient déjà V:{v}
    const prefix = _voicePrefixLine(voiceIdx, instr?.program ?? '');

    const resultLines = [];
    if (prefix) resultLines.push(prefix);
    resultLines.push(...cleanedPreK, ...preK);

    const { headerLines, inlineK } = _applyKPlacement(
        voiceIdx,
        [...resultLines, lines[kIdx], ...postK, ...lines.slice(blockEnd)],
        lines[kIdx]   // on passe la ligne complète, pas juste key
    );

    // Fusionner voiceAttrs sur la ligne V:N
    if (voiceAttrs) {
        const vIdx = headerLines.findIndex(l => /^V:\d+/i.test(l));
        if (vIdx !== -1) headerLines[vIdx] = _mergeVoiceAttrs(headerLines[vIdx], voiceAttrs);
    }

    setVoiceHeaderContent(voiceIdx, headerLines.join('\n'));

    if (inlineK !== null) {
        setVoiceContent(voiceIdx, _injectInlineK(getVoiceContent(voiceIdx), inlineK));
    }

    _updateVoiceLabel(voiceIdx);
}

export function updateHeaderLine(tag, value) {
    const text  = getHeadersContent();
    const lines = text.split('\n');
    const re    = new RegExp('^' + tag + ':');
    const idx   = lines.findIndex(l => re.test(l));

    if (idx !== -1) {
        if (value === '') lines.splice(idx, 1);
        else lines[idx] = tag + ':' + value;
    } else if (value !== '') {
        lines.push(tag + ':' + value);
    }
    setHeadersContent(lines.join('\n'));
}


export function updateSecondTLine(value) {
    const text  = getHeadersContent();
    const lines = text.split('\n');
    const first  = lines.findIndex(l => /^T:/i.test(l));
    if (first === -1) return;

    const second = lines.findIndex((l, i) => i > first && /^T:/i.test(l));

    if (value === '') {
        if (second !== -1) lines.splice(second, 1);
    } else {
        const newLine = 'T:' + value;
        if (second !== -1) {
            lines[second] = newLine;
        } else {
            lines.splice(first + 1, 0, newLine);
        }
    }
    setHeadersContent(lines.join('\n'));
}

// ══════════════════════════════════════════════════════════════════════════════
// CHARGEMENT
// ══════════════════════════════════════════════════════════════════════════════

export function parseHeadersFromAbc(text) {
    const doc = parseAbcDocument(text);

    // Fallback ancienne partition sans marqueurs
    if (!doc.global && doc.voiceHeaders.length === 0) {
        const lines = text.split('\n');
        let kIdx = -1;
        for (let i = 0; i < lines.length; i++) {
            if (/^K:/i.test(lines[i])) kIdx = i;
        }
        if (kIdx !== -1) {
            let blockEnd = kIdx + 1;
            while (blockEnd < lines.length && /^(V:\d+|%%)/.test(lines[blockEnd])) blockEnd++;
            const voiceHeaderLines = lines.slice(kIdx, blockEnd);
            const globalLines      = lines.slice(0, kIdx).filter(
                l => !/^(K:|%%MIDI|%%graceslurs|%%bagpipes)/i.test(l)
            );
            const bodyLines = lines.slice(blockEnd);
            while (bodyLines.length && bodyLines[0].trim() === '') bodyLines.shift();
            doc.global       = globalLines.join('\n');
            doc.voiceHeaders = [voiceHeaderLines.join('\n')];
            doc.voices       = [bodyLines.join('\n')];
        }
    }

    const get = (tag) => {
        const m = doc.global.match(new RegExp('^' + tag + ':(.*)$', 'm'));
        return m ? m[1].trim() : '';
    };
    const tLines = doc.global.match(/^T:(.*)$/gim) ?? [];
    document.getElementById('hf-title').value    = tLines[0] ? tLines[0].slice(2).trim() : '';
    const subtitleEl = document.getElementById('hf-subtitle');
    if (subtitleEl) subtitleEl.value = tLines[1] ? tLines[1].slice(2).trim() : '';
    document.getElementById('hf-composer').value = get('C');
    document.getElementById('hf-source').value   = get('S');
    setMeterPickerValue(get('M') || '4/4');
    setTempoPickerValue(get('Q') || '1/4=80');

    setHeadersContent(doc.global);

// Injecter %%barnumbers 2 si absent
    (function ensureBarnumbers() {
        const current = getHeadersContent();
        if (!/^%%barnumbers\b/m.test(current)) {
            setHeadersContent(current + '\n%%barnumbers 2');
        }
    })();

    buildVoiceBlocks(doc);
    setHeaderFormActive(true);
}

// ══════════════════════════════════════════════════════════════════════════════
// INSTRUMENTS ACTIFS
// ══════════════════════════════════════════════════════════════════════════════

function _refreshActiveInstruments() {
    const instruments = [];
    const count = document.querySelectorAll('.voice-block').length;
    for (let i = 0; i < count; i++) {
        const name = _getInstrumentNameFromHeader(getVoiceHeaderContent(i));
        const inst = INSTRUMENTS.find(x => x.name === name);
        if (inst && !instruments.find(x => x.name === inst.name)) instruments.push(inst);
    }
    setActiveInstruments(instruments);
}

// ══════════════════════════════════════════════════════════════════════════════
// MODALE — Paramètres de partition
// ══════════════════════════════════════════════════════════════════════════════

let _ppState       = null;
let _paramsApplied = false;

export async function openNewPartition() {
    if (!sessionToken) { setPendingAction(openNewPartition); openUserModal(); return; }
    try {
        const res  = await fetch('/api/partitions.php?action=quota', { headers: authHeaders() });
        const data = await res.json();
        if (data.can_create === false) { _showUpgradeModal(data.limit, data.count); return; }
    } catch(e) { console.warn('[quota]', e); }
    _ppState = { mode: 'new', title: '', titleOrig: '', subtitle: '', composer: '', source: '',
                 meter: '4/4', tempo: '1/4=80', unitNote: '' };
    openModal('partition-params', _mountParamsView);
}

export async function openNewPartitionWithInstrument(instrumentName) {
    if (!sessionToken) {
        setPendingAction(() => openNewPartitionWithInstrument(instrumentName));
        openUserModal(); return;
    }
    try {
        const res  = await fetch('/api/partitions.php?action=quota', { headers: authHeaders() });
        const data = await res.json();
        if (data.can_create === false) { _showUpgradeModal(data.limit, data.count); return; }
    } catch(e) { console.warn('[quota]', e); }
    _ppState = { mode: 'new', title: '', titleOrig: '', subtitle: '', composer: '', source: '',
                 meter: '4/4', tempo: '1/4=80', unitNote: '' };
    openModal('partition-params', _mountParamsView);
}

export function openPartitionParams() {
    const global = getHeadersContent();
    const get    = (tag) => { const m = global.match(new RegExp('^' + tag + ':(.*)$', 'm')); return m ? m[1].trim() : ''; };
    const title    = get('T');
    const tLines   = global.match(/^T:(.*)$/gim) ?? [];
    const subtitle = tLines[1] ? tLines[1].slice(2).trim() : '';
    _ppState = {
        mode: 'edit', title, titleOrig: title, subtitle,
        composer: get('C'), source: get('S'),
        meter: get('M') || '4/4', tempo: get('Q') || '1/4=80',
        unitNote: get('L') || '',
    };
    openModal('partition-params', _mountParamsView);
}

export function _doOpenNew() {
    _ppState = { mode: 'new', title: '', titleOrig: '', subtitle: '', composer: '', source: '',
                 meter: '4/4', tempo: '1/4=80', unitNote: '' };
    openModal('partition-params', _mountParamsView);
}

async function _mountParamsView(nav, controller) {
    const { signal } = controller;

    if (document.getElementById('pp-t')) {
        const titleEl = document.getElementById('pp-title');
        const tEl     = document.getElementById('pp-t');
        const t2El = document.getElementById('pp-t2');
        const cEl     = document.getElementById('pp-c');
        const sEl     = document.getElementById('pp-s');
        const errEl   = document.getElementById('pp-error');
        const nextBtn = document.getElementById('pp-next');

        if (titleEl) titleEl.textContent = _ppState.mode === 'edit' ? t('hf.modal_params_edit') : t('hf.modal_params_new');

        tEl.value = _ppState.title    || '';        
        if (t2El) t2El.value = _ppState.subtitle || '';
        cEl.value = _ppState.composer || '';
        sEl.value = _ppState.source   || '';

        _initModalGlobalPickers(signal);
        setTimeout(() => tEl.focus(), 50);

        document.querySelector('.btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal });


        nextBtn?.addEventListener('click', async () => {
            const title = tEl.value.trim();
            if (!title) { errEl.textContent = t('hf.error_title_required'); return; }
            errEl.textContent = '';
            _ppState.title    = title;
            _ppState.subtitle = (document.getElementById('pp-t2')?.value ?? '').trim();
            _ppState.composer = cEl.value.trim();
            _ppState.source   = sEl.value.trim();

            if (_ppState.mode === 'edit') {
                _applyGlobalParamsToEditor();
                if (title === _ppState.titleOrig) {
                    closeModal();
                    return;
                }
                // Titre changé : relancer le process d'assignation de tune
                nextBtn.disabled = true; nextBtn.textContent = t('hf.next_btn_searching');
                try {
                    const res   = await fetch(`/api/tunes.php?search=${encodeURIComponent(title)}`, { headers: authHeaders() });
                    const tunes = await res.json();
                    if (!Array.isArray(tunes) || tunes.length === 0) {
                        await _createTuneAndApply();
                    } else {
                        _ppState.matchedTunes = tunes;
                        nav('partition-tune-match');
                    }
                } catch(e) {
                    errEl.textContent = t('hf.error_network');
                } finally {
                    if (nextBtn) { nextBtn.disabled = false; nextBtn.textContent = t('hf.next_btn_label'); }
                }
                return;
            }

            // mode new
            nextBtn.disabled = true; nextBtn.textContent = t('hf.next_btn_searching');
            try {
                const res   = await fetch(`/api/tunes.php?search=${encodeURIComponent(title)}`, { headers: authHeaders() });
                const tunes = await res.json();
                if (!Array.isArray(tunes) || tunes.length === 0) {
                    await _createTuneAndApply();
                } else {
                    _ppState.matchedTunes = tunes;
                    nav('partition-tune-match');
                }
            } catch(e) {
                errEl.textContent = t('hf.error_network');
            } finally {
                if (nextBtn) { nextBtn.disabled = false; nextBtn.textContent = t('hf.next_btn_label'); }
            }
        }, { signal });
    }

    else if (document.getElementById('ptm-list')) {
        const listEl = document.getElementById('ptm-list');
        const errEl  = document.getElementById('ptm-error');
        const newBtn = document.getElementById('ptm-new-tune');

        listEl.innerHTML = '';
        (_ppState.matchedTunes || []).forEach(tune => {
            const btn = document.createElement('button');
            btn.type = 'button'; btn.className = 'ptm-tune-btn';
            btn.innerHTML = `<span class="ptm-tune-title">${_esc(tune.title)}</span>
                             <span class="ptm-tune-versions">${tune.versions_count || 0} version(s)</span>`;
            btn.addEventListener('click', async () => {
                errEl.textContent = '';
                try { await _applyWithTune(tune.id); }
                catch(e) { errEl.textContent = t('hf.error_tune_attach'); }
            }, { signal });
            listEl.appendChild(btn);
        });

        newBtn?.addEventListener('click', async () => {
            newBtn.disabled = true; newBtn.textContent = ('hf.new_btn_creating');
            try { await _createTuneAndApply(); }
            catch(e) {
                errEl.textContent = t('hf.error_tune_create');
                newBtn.disabled = false; newBtn.textContent = t('hf.new_tune_btn');
            }
        }, { signal });
    }
}


function _initModalGlobalPickers(signal) {
    initMeterPickerFor('pp-meter-trigger', _ppState.meter || '4/4', v => {
        _ppState.meter = v;
        // Mettre à jour l'affichage du trigger note de référence si le défaut change
        updateUnitNotePickerDefault(
            'pp-unit-note-trigger',
            _ppState.unitNote,
            _defaultUnitNote(v)
        );
    });
    initTempoPickerFor('pp-tempo-trigger', _ppState.tempo || '1/4=80', v => { _ppState.tempo = v; });
    initUnitNotePickerFor(
        'pp-unit-note-trigger',
        _ppState.unitNote || '',
        _defaultUnitNote(_ppState.meter || '4/4'),
        v => { _ppState.unitNote = v; }
    );
}

function _applyGlobalParamsToEditor() {
    _paramsApplied = true;
    const { title, subtitle, composer, source, meter, tempo, unitNote } = _ppState;
    const resolvedL = unitNote || _defaultUnitNote(meter);
    updateHeaderLine('T', title);
    updateSecondTLine(subtitle || '');
    updateHeaderLine('C', composer);
    updateHeaderLine('S', source);
    updateHeaderLine('M', meter);
    updateHeaderLine('L', resolvedL);
    updateHeaderLine('Q', tempo);

    document.getElementById('hf-title').value    = title    || '';
    document.getElementById('hf-composer').value = composer || '';
    document.getElementById('hf-source').value   = source   || '';
    setMeterPickerValue(meter);
    setTempoPickerValue(tempo);
    document.getElementById('partition-title-display').textContent = title || '';

    highlightAll(); render();
    refreshAttachButtons(currentId);
    updateOwnerUI();
}

function _buildNewPartition() {
    _paramsApplied = true;
    const { title, subtitle, composer, source, meter, tempo, unitNote } = _ppState;
    const resolvedL = unitNote || _defaultUnitNote(meter);

    let global = `X:1\nT:${title || 'Nouvelle partition'}\n`;
    if (subtitle) global += `T:${subtitle}\n`;
    if (composer) global += `C:${composer}\n`;
    if (source)   global += `S:${source}\n`;
    global += `M:${meter}\nL:${resolvedL}\nQ:${tempo}\n%%barnumbers 2`;

    const defaultInstr = INSTRUMENTS.find(i => i.name === 'bagpipe') ?? INSTRUMENTS[0];
    const key          = defaultInstr?.forcedKey ?? 'HP';

    // Voix 0 : V:1 en tête (sauf si program contient déjà V:{v}), K: dans le header
    const prefix = _voicePrefixLine(0, defaultInstr?.program ?? '');
    const { preK, postK } = _computeContextualDirectives(
        defaultInstr ? [defaultInstr] : [], key, 1
    );

    const headerLines = [];
    if (prefix) headerLines.push(prefix);
    headerLines.push(...preK, `K:${key}`, ...postK);
    const voiceHeader = headerLines.join('\n');
    const voiceBody   = `V:1\n| ABcd |`;

    setCurrentId(null);
    setCurrentSlug(null);
    setCurrentPartitionMeta({ is_owner: true, is_public: false });
    document.getElementById('partition-title-display').textContent = '';
    document.getElementById('save-status').textContent = '';

    setHeadersContent(global);
    buildVoiceBlocks({ voiceHeaders: [voiceHeader], voices: [voiceBody] });

    historyReset(getAbcContent());
    setHeaderFormActive(true);

    document.getElementById('hf-title').value    = title    || '';
    document.getElementById('hf-composer').value = composer || '';
    document.getElementById('hf-source').value   = source   || '';
    setMeterPickerValue(meter);
    setTempoPickerValue(tempo);
    document.getElementById('partition-title-display').textContent = title || '';

    highlightAll(); render();
    refreshAttachButtons(null);
    updateOwnerUI();
}

async function _createTuneAndApply() {
    const res = await fetch('/api/tunes.php', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ title: _ppState.title })
    });
    if (!res.ok) throw new Error('Erreur création tune');
    await _applyWithTune((await res.json()).id);
}


async function _applyWithTune(tuneId) {
    if (currentId) {
        await fetch(`/api/tunes.php?id=${tuneId}`, {
            method: 'PATCH', headers: authHeaders(),
            body: JSON.stringify({ partition_id: currentId })
        });
        _ppState.tuneId = tuneId;
        closeModal();
        return;
    }
    _ppState.tuneId = tuneId;
    // Si l'éditeur est déjà peuplé (import), ne pas reconstruire la partition
    if (!_paramsApplied) {
        _buildNewPartition();
    }
    closeModal();
}

// ══════════════════════════════════════════════════════════════════════════════
// MODALE — Paramètres d'une voix
// ══════════════════════════════════════════════════════════════════════════════

let _vpState = null;

export function openVoiceParams(voiceIdx) {
    const headerText = getVoiceHeaderContent(voiceIdx);
    // Pour les voix 1+, K: est absent du header et stocké en [K:...] dans le corps
    const keyFromHeader = _getHeaderLine(headerText, 'K');
    const keyFromBody   = (() => {
        const m = getVoiceContent(voiceIdx).match(/\[K:([^\]]+)\]/);
        return m ? m[1].trim() : '';
    })();
    const hasKey = !!(keyFromHeader || keyFromBody);
    const bodyText = getVoiceContent(voiceIdx);

    const block      = document.querySelector(`.voice-block[data-voice="${voiceIdx}"]`);
    const staffGroup = block?.dataset.staffGroup ? parseInt(block.dataset.staffGroup) : null;
    _vpState = {
        voiceIdx,
        key:        (voiceIdx > 0 && !hasKey) ? '__inherit__' : (keyFromHeader || keyFromBody || 'HP'),
        instr:      _getInstrumentNameFromHeader(headerText) || (INSTRUMENTS[0]?.name ?? ''),
        name:       _getVoiceNameAttr(headerText),
        clef:       _getVoiceClefAttr(headerText, bodyText) || 'treble',
        stafflines: _getVoiceStafflinesAttr(headerText, bodyText) ?? 5,
        stem:       _getVoiceStemAttr(headerText),
        staffGroup,
    };
    
console.log('[VP open] headerText:', getVoiceHeaderContent(voiceIdx));
console.log('[VP open] _vpState:', JSON.stringify(_vpState));
    openModal('voice-params', _mountVoiceParamsView);
}

async function _mountVoiceParamsView(nav, controller) {
    const { signal } = controller;
    const titleEl    = document.getElementById('vp-title');
    const saveBtn    = document.getElementById('vp-save');
    const deleteBtn  = document.getElementById('vp-delete-btn');
    const deleteRow  = document.getElementById('vp-delete-row');
    const mainPanel  = document.getElementById('vp-main');
    const confirmPanel = document.getElementById('vp-confirm-delete');
    if (!saveBtn) return;

    if (titleEl) titleEl.textContent = t('hf.modal_voice_edit', { n: _vpState.voiceIdx + 1 });

    _initVoicePickerAndInstrument(signal);

    // Afficher le bouton Supprimer seulement si ce n'est pas la seule voix
    if (deleteRow && getVoiceCount() > 1) {
        deleteRow.style.display = '';
    }

    document.querySelector('.btn-cancel')
        ?.addEventListener('click', () => closeModal(), { signal });

    saveBtn.addEventListener('click', () => { _applyVoiceParams(); closeModal(); }, { signal });

    // ── Bouton Supprimer → bascule vers le panneau de confirmation ──
    deleteBtn?.addEventListener('click', () => {
        mainPanel.style.display  = 'none';
        confirmPanel.style.display = '';
        const confirmText = document.getElementById('vp-confirm-text');
        if (confirmText) {
            confirmText.textContent = t('hf.voice_delete_confirm', { n: _vpState.voiceIdx + 1 });

        }
    }, { signal });

    // ── Retour depuis la confirmation ──
    document.getElementById('vp-cancel-delete')
        ?.addEventListener('click', () => {
            confirmPanel.style.display = 'none';
            mainPanel.style.display    = '';
        }, { signal });

    // ── Confirmation effective de la suppression ──
    document.getElementById('vp-confirm-delete-btn')
        ?.addEventListener('click', () => {
            _deleteVoice(_vpState.voiceIdx);
            closeModal();
        }, { signal });
}

function _initVoicePickerAndInstrument(signal) {
    const _instrForPicker = () => INSTRUMENTS.find(i => i.name === _vpState.instr);
    initKeyPickerFor(
        'vp-key-trigger',
        _vpState.key,
        (abc) => { _vpState.key = abc; _vpState._keySetByUser = true; },
        {
            allowInherit:  _vpState.voiceIdx > 0,
            suggestedKeys: _instrForPicker()?.suggestedKeys ?? [],
        }
    );

    const sel        = document.getElementById('vp-instrument');
    const keyField   = document.getElementById('vp-key-field');
    const keyForced  = document.getElementById('vp-key-forced');
    const keyLabel   = document.getElementById('vp-forced-key-value');
    const nameCheck  = document.getElementById('vp-name-check');
    const nameInput  = document.getElementById('vp-name');
    const clefSel  = document.getElementById('vp-clef');
    const staffSel = document.getElementById('vp-stafflines');
    const stemSel  = document.getElementById('vp-stem');



    // ── Initialiser la case à cocher EN PREMIER ──────────────────────────────
    // Doit être fait avant _applyInstrumentConstraints pour que
    // nameCheck.checked soit déjà à jour quand la contrainte est appliquée.
    if (nameCheck && nameInput) {
        const hasName      = !!_vpState.name;
        nameCheck.checked  = hasName;
        nameInput.value    = hasName
            ? _vpState.name
            : (INSTRUMENTS.find(i => i.name === _vpState.instr)?.label ?? '');
        nameInput.readOnly = !hasName;
        nameInput.classList.toggle('vp-name-readonly', !hasName);

        nameCheck.addEventListener('change', function () {
            const checked      = this.checked;
            nameInput.readOnly = !checked;
            nameInput.classList.toggle('vp-name-readonly', !checked);
            if (checked) {
                if (!nameInput.value.trim()) {
                    const inst = INSTRUMENTS.find(i => i.name === _vpState.instr);
                    nameInput.value = inst?.label ?? '';
                }
                _vpState.name = nameInput.value.trim();
                nameInput.focus();
                nameInput.select();
            } else {
                _vpState.name = '';
            }
        }, { signal });

        nameInput.addEventListener('input', function () {
            if (nameCheck.checked) _vpState.name = this.value.trim();
        }, { signal });
    }

    const _keyWasUserDefined = () =>
        _vpState.key !== '__inherit__' &&
        _vpState.key !== '' &&
        _vpState._keySetByUser === true;

    // ── Contraintes instrument ───────────────────────────────────────────────
function _applyInstrumentConstraints(slug, isUserChange = false) {
        const inst        = INSTRUMENTS.find(i => i.name === slug);
        const forcedKey   = inst?.forcedKey ?? null;
        const suggested   = inst?.suggestedKeys ?? [];

        // ── Clé forcée ──
        if (forcedKey) {
            keyField.style.display  = 'none';
            keyForced.style.display = '';
            keyLabel.textContent    = forcedKey;
            _vpState.key            = forcedKey;
        } else {
            keyField.style.display  = '';
            keyForced.style.display = 'none';

            // ── Pré-sélection automatique uniquement si :
            //    - c'est un changement d'instrument par l'utilisateur (isUserChange)
            //    - ET la tonalité n'a pas encore été choisie manuellement
            if (isUserChange && !_vpState._keySetByUser) {
                let autoKey = null;
                if (suggested.length > 0) {
                    autoKey = suggested[0] === 'none' ? 'none' : suggested[0];
                }
                if (autoKey !== null) {
                    _vpState.key = autoKey;
                    // Mettre à jour le picker
                    const pkInst = getKeyPickerInstance('vp-key-trigger');
                    if (pkInst) {
                        pkInst.suggestedKeys = suggested;
                        pkInst.currentAbc    = autoKey;
                        pkInst.mode          = autoKey === 'none' ? 'none'
                            : (suggested.includes(autoKey) ? 'instrument' : 'major');
                        // Mettre à jour le texte du trigger
                        const trigger = document.getElementById('vp-key-trigger');
                        if (trigger) trigger.textContent = autoKey === 'none' ? 'Aucune' : autoKey;
                    }
                }

                // Pré-sélection stafflines si l'instrument en définit une
                if (isUserChange && !_vpState._stafflinesSetByUser) {
                    const defSl = inst?.defaultStafflines ?? null;
                    if (defSl !== null) {
                        _vpState.stafflines = defSl;
                        if (staffSel) staffSel.value = String(defSl);
                    } else {
                        _vpState.stafflines = 5;
                        if (staffSel) staffSel.value = '5';
                    }
                }

                if (isUserChange && !_vpState._clefSetByUser) {
                    const defClef = inst?.defaultClef ?? null;
                    _vpState.clef = defClef || 'treble';
                    if (clefSel) clefSel.value = _vpState.clef;
                }

            }
        }

        // ── Mettre à jour suggestedKeys dans le picker (toujours) ──
        const pkInst = getKeyPickerInstance('vp-key-trigger');
        if (pkInst) pkInst.suggestedKeys = suggested;

        // ── Nom ──
        if (nameInput && !nameCheck?.checked) {
            const label = inst?.label ?? '';
            nameInput.placeholder = label || 'Cornemuse';
            nameInput.value       = label;
        }
    }

    if (sel) {
        sel.value = _vpState.instr || '';
        _applyInstrumentConstraints(sel.value);
        sel.addEventListener('change', function () {
            _vpState.instr       = this.value;
            _vpState._keySetByUser  = false;
            _vpState._clefSetByUser = false;
            _applyInstrumentConstraints(this.value, true);
        }, { signal });
    }

    if (clefSel) {
        clefSel.value = _vpState.clef || 'treble';
        clefSel.addEventListener('change', function () {
            _vpState.clef          = this.value;
            _vpState._clefSetByUser = true;
        }, { signal });
    }

    if (staffSel) {
        staffSel.value = String(_vpState.stafflines ?? 5);
        staffSel.addEventListener('change', function () {
            _vpState.stafflines         = parseInt(this.value);
            _vpState._stafflinesSetByUser = true;
        }, { signal });
    }    

    if (stemSel) {
        stemSel.value = _vpState.stem || 'auto';
        stemSel.addEventListener('change', function () {
            _vpState.stem = this.value;
        }, { signal });
    }

    const staffGroupSel = document.getElementById('vp-staff-group');
    if (staffGroupSel) {
        staffGroupSel.value = _vpState.staffGroup != null ? String(_vpState.staffGroup) : '';
        staffGroupSel.addEventListener('change', function () {
            _vpState.staffGroup = this.value ? parseInt(this.value) : null;
        }, { signal });
    }



    // ── Checkbox "Hériter la tonalité" (voix 1+ seulement) ──────────────────
    const inheritRow    = document.getElementById('vp-key-inherit-row');
    const inheritCheck  = document.getElementById('vp-key-inherit');
    const keyTrigger    = document.getElementById('vp-key-trigger');

    if (_vpState.voiceIdx > 0 && inheritRow && inheritCheck && keyTrigger) {
        inheritRow.style.display = '';

        inheritCheck.checked    = !!_vpState.inheritKey;
        keyTrigger.disabled     = !!_vpState.inheritKey;
        keyTrigger.classList.toggle('kp-trigger-disabled', !!_vpState.inheritKey);

        inheritCheck.addEventListener('change', function () {
            _vpState.inheritKey     = this.checked;
            keyTrigger.disabled     = this.checked;
            keyTrigger.classList.toggle('kp-trigger-disabled', this.checked);
        }, { signal });
    } 

}

// Remplacer TOUT ce qui se trouve entre la signature de _applyVoiceParams()
// et le bloc "Fusionner voiceAttrs" (inclus) par ce qui suit :

function _applyVoiceParams() {
    const { voiceIdx, key, instr } = _vpState;
    const inst     = INSTRUMENTS.find(i => i.name === instr) ?? INSTRUMENTS[0];
    const selected = inst ? [inst] : [];

    const { preK, postK, voiceAttrs } = _computeContextualDirectives(selected, key, voiceIdx + 1);

    const voiceNum   = voiceIdx + 1;
    const namePart   = _vpState.name ? ` name="${_vpState.name}"` : '';
    const isInherit  = _vpState.key === '__inherit__';
    const isNone     = _vpState.key === 'none';
    const clef       = _vpState.clef       || 'treble';
    const stafflines = _vpState.stafflines ?? 5;
    const hasVisualAttrs = (clef !== 'treble') || (stafflines !== 5);

    // V: porte name= et stem= (si non-auto)
    const stemPart   = (_vpState.stem && _vpState.stem !== 'auto') ? ` stem=${_vpState.stem}` : '';
    const postKLines = postK.flatMap(s => s.split('\n'));
    const vLineIdx   = postKLines.findIndex(l => /^V:\d+/i.test(l));
    if (vLineIdx !== -1) {
        postKLines[vLineIdx] = postKLines[vLineIdx]
            .replace(/^(V:\d+)(.*)$/, `$1${namePart}${stemPart}$2`);
    }

    // Construire la ligne K: complète (avec clef= et stafflines= si non-défaut)
    const kLine = isInherit
        ? _buildKLine('', clef, stafflines)
        : isNone
            ? _buildKLine('none', clef, stafflines)
            : _buildKLine(key, clef, stafflines);

    let rawLines;
    if (isInherit && !hasVisualAttrs) {
        // Héritage pur — pas de K: du tout
        rawLines = [...preK, ...postKLines];
    } else {
        rawLines = [...preK, kLine, ...postKLines];
    }

    if (vLineIdx === -1) {
        rawLines.unshift(`V:${voiceNum}${namePart}${stemPart}`);
    }

    const { headerLines, inlineK } = (isInherit && !hasVisualAttrs)
        ? { headerLines: rawLines, inlineK: null }
        : _applyKPlacement(voiceIdx, rawLines, kLine);

    // Fusionner voiceAttrs sur la ligne V:N
    if (voiceAttrs) {
        const vIdx = headerLines.findIndex(l => /^V:\d+/i.test(l));
        if (vIdx !== -1) headerLines[vIdx] = _mergeVoiceAttrs(headerLines[vIdx], voiceAttrs);
    }

    setVoiceHeaderContent(voiceIdx, headerLines.join('\n'));
    _updateScoreDirective();   // ← ajouter cette ligne

    if (inlineK !== null) {
        setVoiceContent(voiceIdx, _injectInlineK(getVoiceContent(voiceIdx), inlineK));
    }

    const block = document.querySelector(`.voice-block[data-voice="${voiceIdx}"]`);
    if (block && inst) block.dataset.instrumentId = inst.id;
    if (block) {
        if (_vpState.staffGroup != null) block.dataset.staffGroup = String(_vpState.staffGroup);
        else delete block.dataset.staffGroup;
    }

    _updateVoiceLabel(voiceIdx);
    _refreshActiveInstruments();
    highlightAll();
    render();
}

/**
 * Supprime la voix à l'index donné et renuméroite les voix suivantes.
 */
function _deleteVoice(voiceIdx) {
    const container = document.getElementById('voices-container');
    if (!container) return;

    const blocks = [...container.querySelectorAll('.voice-block')];
    if (blocks.length <= 1) return; // sécurité : ne jamais supprimer la dernière voix

    // Retirer le bloc du DOM
    const target = container.querySelector(`.voice-block[data-voice="${voiceIdx}"]`);
    if (target) target.remove();

    // Renuméroter tous les blocs restants
    const remaining = [...container.querySelectorAll('.voice-block')];
    remaining.forEach((block, newIdx) => {
        const oldIdx = parseInt(block.dataset.voice);

        // Mettre à jour data-voice
        block.dataset.voice = newIdx;

        // Renommer les ids des éléments internes
        const headerEl = block.querySelector(`#abc-voice-header-${oldIdx}`);
        const bodyEl   = block.querySelector(`#abc-voice-${oldIdx}`);
        if (headerEl) headerEl.id = `abc-voice-header-${newIdx}`;
        if (bodyEl)   bodyEl.id   = `abc-voice-${newIdx}`;

        // Renuméroter V:N dans le contenu ABC
                _renumberVoice(newIdx);

        // Mettre à jour le label de la voix
        _updateVoiceLabel(newIdx);
    });

    _refreshVisibilityButtons();
    _refreshActiveInstruments();
    highlightAll();
    render();
}

/**
 * Génère et injecte %%score dans le header global
 * en fonction des groupes de portées (staffGroup) de chaque voix.
 * Les voix sans staffGroup forment chacune leur propre staff.
 * Les voix ayant le même staffGroup (entier > 0) sont regroupées entre parenthèses.
 *
 * Exemple : voix 0 → group null, voix 1 → group 1, voix 2 → group 1
 *   → %%score 1 (2 3)
 */
function _updateScoreDirective() {
    const count  = getVoiceCount();
    if (count <= 1) {
        // Une seule voix : pas besoin de %%score
        const current = getHeadersContent();
        setHeadersContent(current.replace(/^%%score\b.*$/m, '').replace(/\n{3,}/g, '\n\n'));
        return;
    }

    // Lire le staffGroup de chaque voix (stocké en data-staff-group sur le bloc)
    const groups = [];
    for (let i = 0; i < count; i++) {
        const block = document.querySelector(`.voice-block[data-voice="${i}"]`);
        const g     = block?.dataset.staffGroup ? parseInt(block.dataset.staffGroup) : null;
        groups.push(g);
    }

    // Construire les tokens %%score
    const seen   = new Set();
    const tokens = [];
    for (let i = 0; i < count; i++) {
        const g = groups[i];
        if (g === null) {
            tokens.push(String(i + 1));
        } else {
            if (!seen.has(g)) {
                seen.add(g);
                // Collecter toutes les voix de ce groupe
                const members = groups
                    .map((gg, idx) => gg === g ? String(idx + 1) : null)
                    .filter(Boolean);
                tokens.push(members.length > 1 ? `(${members.join(' ')})` : members[0]);
            }
            // Les suivantes du même groupe sont déjà incluses
        }
    }

    const directive = `%%score ${tokens.join(' ')}`;
    const current   = getHeadersContent();
    if (/^%%score\b/m.test(current)) {
        setHeadersContent(current.replace(/^%%score\b.*$/m, directive));
    } else {
        setHeadersContent(current + '\n' + directive);
    }
}


// ══════════════════════════════════════════════════════════════════════════════
// BOUTON "Ajouter une voix"
// ══════════════════════════════════════════════════════════════════════════════

export function initAddVoiceButton() {
    document.getElementById('btn-add-voice')
        ?.addEventListener('click', () => {
            _vpState = { voiceIdx: getVoiceCount(), key: 'HP', instr: INSTRUMENTS[0]?.name ?? '' };
            openModal('voice-params', async (nav, controller) => {
                const { signal } = controller;
                const titleEl    = document.getElementById('vp-title');
                const saveBtn    = document.getElementById('vp-save');
                if (titleEl) titleEl.textContent = t('hf.modal_voice_new');
                _initVoicePickerAndInstrument(signal);
                document.querySelector('.btn-cancel')
                    ?.addEventListener('click', () => closeModal(), { signal });
                saveBtn?.addEventListener('click', () => {
                    // Pas de \n entre V:N et les notes — _applyVoiceParams injectera [K:] juste après V:N
                    appendVoiceBlock('', `V:${_vpState.voiceIdx + 1}\n| ABcd |`);
                    _applyVoiceParams();
                    closeModal();
                }, { signal });
            });
        });
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPATIBILITÉ
// ══════════════════════════════════════════════════════════════════════════════

export function getSelectedInstruments() {
    const count = document.querySelectorAll('.voice-block').length;
    const result = [];
    for (let i = 0; i < count; i++) {
        const name = _getInstrumentNameFromHeader(getVoiceHeaderContent(i));
        const inst = INSTRUMENTS.find(x => x.name === name);
        if (inst && !result.find(x => x.name === inst.name)) result.push(inst);
    }
    return result;
}

export function updateInstrumentFromForm() {
    const count = document.querySelectorAll('.voice-block').length;
    for (let i = 0; i < count; i++) updateVoiceFromForm(i);
}

export function applyInstrumentToVoice(voiceIdx, instrumentName) {
    const inst = INSTRUMENTS.find(i => i.name === instrumentName);
    if (!inst) return;

    const headerText = getVoiceHeaderContent(voiceIdx);
    const keyFromHeader = _getHeaderLine(headerText, 'K');
    const keyFromBody   = (() => {
        const m = getVoiceContent(voiceIdx).match(/\[K:([^\]]+)\]/);
        return m ? m[1].trim() : '';
    })();
    const key = keyFromHeader || keyFromBody || inst.forcedKey || 'HP';

    const { preK, postK, voiceAttrs } = _computeContextualDirectives([inst], key, voiceIdx + 1);

    const prefix = _voicePrefixLine(voiceIdx, inst.program);
    const rawLines = [];
    if (prefix) rawLines.push(prefix);
    rawLines.push(...preK, `K:${key}`, ...postK);

    const { headerLines, inlineK } = _applyKPlacement(voiceIdx, rawLines, `K:${key}`);

    if (voiceAttrs) {
        const vIdx = headerLines.findIndex(l => /^V:\d+/i.test(l));
        if (vIdx !== -1) headerLines[vIdx] = _mergeVoiceAttrs(headerLines[vIdx], voiceAttrs);
    }

    setVoiceHeaderContent(voiceIdx, headerLines.join('\n'));

    if (inlineK !== null) {
        setVoiceContent(voiceIdx, _injectInlineK(getVoiceContent(voiceIdx), inlineK));
    }

    const block = document.querySelector(`.voice-block[data-voice="${voiceIdx}"]`);
    if (block) block.dataset.instrumentId = inst.id;
    _updateVoiceLabel(voiceIdx);
    _refreshActiveInstruments();
    _refreshVisibilityButtons();
}

export async function startImportTuneFlow(title) {
    // Initialiser _ppState avec le titre extrait de l'ABC importé
    _ppState = {
        mode: 'new', title, titleOrig: '', subtitle: '', composer: '', source: '',
        meter: '4/4', tempo: '1/4=80', unitNote: '',
    };
    _paramsApplied = true; // l'éditeur est déjà peuplé, on ne repart pas de zéro

    try {
        const res   = await fetch(`/api/tunes.php?search=${encodeURIComponent(title)}`, { headers: authHeaders() });
        const tunes = await res.json();
        if (!Array.isArray(tunes) || tunes.length === 0) {
            await _createTuneAndApply();
        } else {
            _ppState.matchedTunes = tunes;
            openModal('partition-tune-match', _mountParamsView);
        }
    } catch (e) {
        console.warn('[import tune flow]', e);
    }
}


// ══════════════════════════════════════════════════════════════════════════════
// UTILITAIRES EXPORTÉS
// ══════════════════════════════════════════════════════════════════════════════

export function wasParamsApplied()   { return _paramsApplied; }
export function resetParamsApplied() { _paramsApplied = false; }
export function getPendingTuneId()   { return _ppState?.tuneId ?? null; }
export function clearPendingTuneId() { if (_ppState) _ppState.tuneId = null; }

function _esc(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}


/**
 * Calcule la note de référence par défaut selon la spec ABC 2.1 :
 * si M < 0.75 → 1/16, sinon → 1/8.
 * Retourne '1/8' ou '1/16'.
 */
function _defaultUnitNote(meter) {
    if (!meter) return '1/8';
    const m = meter.match(/^(\d+)\/(\d+)$/);
    if (!m) return '1/8';
    return (parseInt(m[1]) / parseInt(m[2])) < 0.75 ? '1/16' : '1/8';
}


function _showUpgradeModal(limit, count) {
    openModal('upgrade', (nav, controller) => {
        const el = document.getElementById('upgrade-message');
        if (el) el.textContent = t('hf.upgrade_msg', {
            limit,
            limit_s: limit > 1 ? 's' : '',
            count,
            count_s: count > 1 ? 's' : '',
        });
        document.querySelector('.btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal: controller.signal });
    });
}